/**
 * Auth Service
 *
 * Handles post-login user-details fetching.
 * Separated from OIDC service so credential-based login has its own clean service layer.
 */
import { config as appConfig, API_ROUTES } from '@/core/config';
import { CONTROL_PLANE_TENANT_ID } from '@/modules/users/constants';
import axios from 'axios';

// ── Types ────────────────────────────────────────────────────────────────────

/** User details returned by GET /onboarding/api/v1/users/:username */
export type UserDetailsResponse = {
  sub: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  tenant_id: string;
  hospital_id: string;
  role: string;
  created_at: string;
  job_title?: string;
  department?: string;
  employee_number?: string;
  phone_number?: string;
  [key: string]: unknown;
};

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch full user details from the onboarding service.
 * Called after successful login to get hospital_id, staff_id (sub), role, etc.
 *
 * Endpoint: GET /onboarding/api/v1/users/:username?tenant_id=<uuid>
 *
 * Uses a plain axios call (with withCredentials for the httpOnly PHX-1180
 * auth cookie) so that it bypasses the singleton API client's 401
 * interceptor, which would clear auth state if this request fails during
 * the login flow.
 */
export async function fetchUserDetails(
  username: string,
  tenantId: string,
): Promise<UserDetailsResponse> {
  const base = appConfig.api.baseUrl.replace(/\/+$/, '');
  const { data } = await axios.get<{ success: boolean; data: UserDetailsResponse }>(
    `${base}${API_ROUTES.ONBOARDING}/users/${encodeURIComponent(username)}`,
    {
      params: {
        tenant_id: tenantId,
        ...(tenantId === CONTROL_PLANE_TENANT_ID ? { apptype: 'CONTROLPLANE' } : {}),
      },
      withCredentials: true,
      headers: {
        'x-tenant-hostname': window.location.hostname,
      },
    },
  );
  return data.data;
}
